import { Explore, Query, Timezone } from "@aidkitorg/types/lib/survey";
import { CompileExpressionToSQL, findFields, simulateDate } from "@aidkitorg/types/lib/translation/expr_to_sql";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useState, useMemo, useEffect } from "react";
import { usePost } from "../API";
import ExploreLink, { DistroPreviewModal } from "../Components/ExploreLink";
import { classNames } from "../Util";
import { SqlViewer } from "./CommonComponents";
import { EligibilityChecker } from "./EligibilityChecker";
import { Button, Switch, Badge, DateTimeInput, getBrowserTimezone } from "@aidkitorg/component-library";
import { EnableKey } from "./EnableKey";
import { HasEnableKey } from "@aidkitorg/types/lib/eval";

type QueryCheckerProps = {
  name: string,
  onNameClick?: ()=>void,
  testName: string,
  expr: Query,
  targetField: string,
  component: HasEnableKey
};

export function QueryChecker(props: QueryCheckerProps) {
  const { name, onNameClick, testName, expr, targetField, component } = props;
  const testExpression = usePost('/program/admin/test_expression');
  const [count, setCount] = useState<number | undefined | null>();
  const [error, setError] = useState<string | undefined | null>();
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState(false);
  const [simulationDate, setSimulationDate] = useState<Date>();
  const [modifiedExpr, setModifiedExpr] = useState<Query>(expr);
  const [showEligibility, setShowEligibility] = useState(false);
  const [checkEligibility, setCheckEligibility] = useState(false);
  const [selectedTimestamp, setSelectedTimestamp] = useState<string | undefined>(() => new Date().toISOString());
  const [selectedTimezone, setSelectedTimezone] = useState<Timezone>(() => getBrowserTimezone());

  const sql = useMemo(() =>
    modifiedExpr.kind === 'SQL' ? modifiedExpr.sql : CompileExpressionToSQL({
      cond: modifiedExpr.expr,
      orderBy: modifiedExpr.orderBy
    }),
  [modifiedExpr]
  );

  const query: Explore = useMemo(() => {
    if (modifiedExpr.kind === 'Click') {
      let fields: string[] = findFields(modifiedExpr.expr);
      if (!fields.length) {
        fields = ['legal_name'];
      }
      fields.push(targetField);

      return {
        query: {
          kind: 'Applicant Table',
          filter: modifiedExpr,
          columns: fields.map(f => ({ kind: 'Field', field: f })),
          title: { en: name },
          download: {
            filename: name
          }
        }
      };
    } else {
      return {
        query: {
          kind: 'Custom Query',
          sql: modifiedExpr.sql,
          visualization: {
            kind: 'Table',
            title: { en: name },
            download: {
              filename: name
            }
          }
        }
      };
    }
  }, [modifiedExpr]);

  async function simulate(date?: Date) {
    setChecking(true);
    let newExpr = expr;
    switch (expr.kind) {
      case 'Click':
        if (date) {
          newExpr = { ...expr, expr: simulateDate(expr.expr, date) }
        }
        break;
      default:
        break;
    }
    const simulatedTest = await testExpression({
      query: newExpr
    });
    setModifiedExpr(newExpr);
    setSimulationDate(date);
    setCount(simulatedTest.count);
    setError((simulatedTest as any).error);
    setChecking(false);
  }

  // Run simulation when timestamp or expression changes
  useEffect(() => {
    if (!checkEligibility) {
      return;
    }
    if (!selectedTimestamp) {
      setCount(undefined);
      setError(undefined);
      return;
    }
    simulate(new Date(selectedTimestamp));
  }, [selectedTimestamp, expr, checkEligibility]);

  return <ul className="border border-gray-300 p-2 space-x-3 space-y-2">
    <li className="flex space-x-2">
      {onNameClick ? <a href="#" onClick={onNameClick}><h4>{name} ({targetField})</h4></a> : <h4>{name} ({targetField})</h4>}
      <DistroPreviewModal
        query={query}
        open={preview}
        onClose={setPreview}
      />
      <ExploreLink query={query} />
    </li>
    <li className="flex space-x-1">
      <EnableKey component={component} />
    </li>
    <li>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setCheckEligibility(!checkEligibility)}
      >
        {checkEligibility ? 'Hide Eligibility' : 'Check Eligibility'}
      </Button>
    </li>
    {checkEligibility && (
      <>
        <li>
          <DateTimeInput
            label="Simulation Date"
            showLabel={false}
            value={selectedTimestamp}
            onChange={(isoString) => setSelectedTimestamp(isoString ?? undefined)}
            timezone={selectedTimezone}
            onTimezoneChange={setSelectedTimezone}
            setToNowLabel="Reset to Now"
          />
        </li>
        <li>
          <div className="flex items-center space-x-2">
            <Badge variant="info" className="text-lg px-2 py-1 space-x-2">
              <span>{count ?? '...'}&nbsp;{testName}</span>
              <ArrowPathIcon className={classNames("h-5 w-5", checking ? "animate-spin" : "hidden")} />
            </Badge>
          </div>
          <span className="text-sm bg-red-300 text-wrap" hidden={!error}>{error}</span>
        </li>
      </>
    )}
    <li className="flex space-x-2 justify-end">
      <Button variant="secondary" size="md" onClick={() => setPreview(true)}>
        <span className="size-fit">Preview Explore</span>
      </Button>
    </li>
    <li className="space-y-2">
      <Switch label="Show Eligibility" defaultChecked={showEligibility} onCheckedChange={setShowEligibility} />
      <EligibilityChecker cond={modifiedExpr} show={showEligibility} />
      <SqlViewer sql={sql} />
    </li>
  </ul >
}
